import {
  TypeNavLinkListFields,
  TypeNavLinkPanelFields,
} from "@customTypes/contentful";
import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useContext,
  useRef,
  useState,
} from "react";

export type MobileHistory = ((
  | TypeNavLinkPanelFields
  | TypeNavLinkListFields
) & {
  visibility: boolean;
  items?: (TypeNavLinkPanelFields | TypeNavLinkListFields)[];
})[];

export type OpenedSubmenu = Record<number, number>;

export type MegaNameItem = {
  component: "navLinkList" | "navLinkPanel" | "navGroupHeading" | "generalLink";
  fields?: {
    linkList?: MegaNameItem[];
  };
};

export const getInitialSubmenuIndexes = (
  selected: MegaNameItem,
  acc: number[] = []
): number[] => {
  if (!selected || !selected.component) {
    return acc;
  }

  if (selected.component === "navLinkList") {
    const filtered = selected.fields?.linkList?.filter(
      (item) => item.component === "navLinkList"
    );
    if (filtered?.length > 0) {
      const index = selected.fields?.linkList?.indexOf(filtered[0]) ?? 0;
      return getInitialSubmenuIndexes(filtered[0], [...acc, index]);
    } else {
      return acc;
    }
  }

  if (selected.component === "navLinkPanel") {
    return [...acc, 0];
  }

  return acc;
};

export interface NavigationState {
  mobileMenuHistory: MobileHistory;
  setMobileMenuHistory: Dispatch<SetStateAction<MobileHistory>>;

  setShowSearch: Dispatch<SetStateAction<boolean>>;
  setShowDesktop: Dispatch<SetStateAction<boolean>>;
  setShowPromo: Dispatch<SetStateAction<boolean>>;
  setShowMobile: Dispatch<SetStateAction<boolean>>;
  setOpenMobileMenu: Dispatch<SetStateAction<boolean>>;
  setMainNavSelected: Dispatch<SetStateAction<boolean>>;
  setMainNavSelectedIndex: Dispatch<SetStateAction<number>>;
  setOpenSubmenus: Dispatch<SetStateAction<OpenedSubmenu>>;
  showSearch: boolean;
  showDesktop: boolean;
  showPromo: boolean;
  showMobile: boolean;
  isOpenMobileMenu: boolean;
  mainNavSelected: boolean;
  mainNavSelectedIndex: number | null;
  handleEnter: (index: number) => void;
  handleLeave: () => void;
  handleReset: () => void;
  navHeight: number;
  setNavHeight: Dispatch<SetStateAction<number>>;
  openSubmenus: Record<number, number>; // level -> index of opened submenu at that level
  setOpenSubmenu: (level: number, submenuIndex: number) => void;
  isSubmenuOpen: (level: number, submenuIndex: number) => boolean;
}

export const initialState: NavigationState = {
  mobileMenuHistory: [],
  setMobileMenuHistory: () => {},

  showSearch: false,
  setShowSearch: () => {},
  showDesktop: false,
  setShowDesktop: () => {},
  showMobile: false,
  setShowMobile: () => {},
  isOpenMobileMenu: false,
  setOpenMobileMenu: () => {},
  mainNavSelected: false,
  setMainNavSelected: () => {},
  mainNavSelectedIndex: null,
  setMainNavSelectedIndex: () => {},
  handleEnter: () => {},
  handleLeave: () => {},
  handleReset: () => {},
  navHeight: 350,
  setNavHeight: () => {},
  showPromo: true,
  setShowPromo: () => {},
  openSubmenus: {},
  setOpenSubmenu: () => {},
  isSubmenuOpen: () => false,
  setOpenSubmenus: () => {},
};

export const NavigationStateContext =
  createContext<NavigationState>(initialState);

export const useNavigationStateContext = () =>
  useContext(NavigationStateContext);

type Props = {
  children: ReactNode;
};

export const NavigationStateProvider: FC<Props> = ({ children }) => {
  const [mobileMenuHistory, setMobileMenuHistory] = useState<MobileHistory>(
    initialState.mobileMenuHistory
  );
  const [showSearch, setShowSearch] = useState(initialState.showSearch);
  const [showPromo, setShowPromo] = useState(initialState.showSearch);
  const [showDesktop, setShowDesktop] = useState(initialState.showDesktop);
  const [showMobile, setShowMobile] = useState(initialState.showMobile);
  const [isOpenMobileMenu, setOpenMobileMenu] = useState(
    initialState.isOpenMobileMenu
  );
  const [mainNavSelected, setMainNavSelected] = useState(
    initialState.mainNavSelected
  );
  const [mainNavSelectedIndex, setMainNavSelectedIndex] = useState<
    NavigationState["mainNavSelectedIndex"]
  >(initialState.mainNavSelectedIndex);
  const [navHeight, setNavHeight] = useState(initialState.navHeight);
  const [openSubmenus, setOpenSubmenus] = useState<OpenedSubmenu>(
    initialState.openSubmenus
  );

  // Function to set submenu open at a specific level
  const setOpenSubmenu = (level: number, submenuIndex: number) => {
    setOpenSubmenus((prev) => {
      // Copy prev state
      const updated = { ...prev, [level]: submenuIndex };

      // Close all deeper levels beyond current level
      Object.keys(updated).forEach((key) => {
        const keyNum = Number(key);
        if (keyNum > level) {
          delete updated[keyNum];
        }
      });

      return updated;
    });
  };

  // Check if submenu is open; level 1 always open
  const isSubmenuOpen = (level: number, submenuIndex: number): boolean => {
    return openSubmenus[level] === submenuIndex;
  };

  const hideTimer = useRef<null | ReturnType<typeof setTimeout>>(null);

  const handleEnter = (index: number) => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
    }

    setShowDesktop(true);
    setMainNavSelected(true);
    setMainNavSelectedIndex(index);
  };

  const handleLeave = () => {
    hideTimer.current = setTimeout(() => {
      setShowDesktop(false);
      setMainNavSelected(false);
      setMainNavSelectedIndex(null);
      setNavHeight(350);
      setOpenSubmenus({});
    }, 600);
  };

  const handleReset = () => {
    setOpenMobileMenu(false);
    setShowSearch(false);
    setShowDesktop(false);
    setShowMobile(false);
    setMainNavSelected(false);
    setMainNavSelectedIndex(null);
    setOpenSubmenus({});
    setNavHeight(0);
  };

  return (
    <NavigationStateContext.Provider
      value={{
        mobileMenuHistory,
        setMobileMenuHistory,

        showSearch,
        setShowSearch,
        showPromo,
        setShowPromo,
        showDesktop,
        setShowDesktop,
        showMobile,
        setOpenMobileMenu,
        isOpenMobileMenu,
        setShowMobile,
        mainNavSelected,
        setMainNavSelected,
        mainNavSelectedIndex,
        setMainNavSelectedIndex,
        setOpenSubmenus,
        handleEnter,
        handleLeave,
        handleReset,
        navHeight,
        setNavHeight,
        openSubmenus,
        setOpenSubmenu,
        isSubmenuOpen,
      }}
    >
      {children}
    </NavigationStateContext.Provider>
  );
};

export const mockNavigationStatesContext = {};
